import { FC, MouseEventHandler } from 'react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';
import { ReactComponent as SuccessIcon } from 'src/assets/svg/success.svg';
import { ReactComponent as TimeSmallIcon } from 'src/assets/svg/time-small.svg';
import { ReactComponent as PlusIcon } from 'src/assets/svg/plus.svg';

import { EventAttendanceStatus, EventStatus } from '../../api';
import { getAttendButtonState } from '../../utils/get-attend-button-state';
import { AttendButtonState } from '../../constants/attend-button-state';

export const EventQuickAttendButton: FC<{
    attendanceStatus: EventAttendanceStatus;
    status: EventStatus;
    externalUrl: string | null;
    onOpenCancelAttendanceModal: VoidFunction;
    onOpenLeaveWaitlistModal: VoidFunction;
    onOpenAttendModal: MouseEventHandler;
    onOpenWaitlistModal: VoidFunction;
}> = ({
    attendanceStatus,
    status,
    externalUrl,
    onOpenCancelAttendanceModal,
    onOpenLeaveWaitlistModal,
    onOpenAttendModal,
    onOpenWaitlistModal,
}) => {
    const { palette } = useTheme();
    const { t } = useTranslation();

    const buttonState = getAttendButtonState(status, attendanceStatus);

    switch (buttonState) {
        case AttendButtonState.Attending:
            return (
                <Button
                    onClick={onOpenCancelAttendanceModal}
                    design="success"
                    endIcon={<SuccessIcon width="16px" height="16px" />}
                >
                    {t('event_quick_attending_cta')}
                </Button>
            );
        case AttendButtonState.OnWaitingList:
            return (
                <Button
                    onClick={onOpenLeaveWaitlistModal}
                    design="primary"
                    endIcon={<TimeSmallIcon width="16px" height="16px" />}
                    css={{
                        backgroundColor: palette.warning.dark,
                        color: palette.text.primary,
                    }}
                >
                    {t('events_attendance-action_pending_cta')}
                </Button>
            );
        case AttendButtonState.NotOnWaitingList:
            return (
                <Button
                    design="light"
                    bordered
                    endIcon={<PlusIcon width="16px" height="16px" />}
                    onClick={onOpenWaitlistModal}
                >
                    {t('events_attendance-action_waitlist_cta')}
                </Button>
            );
        case AttendButtonState.NotAttending:
        default:
            return (
                <Button
                    {...(externalUrl && {
                        href: externalUrl,
                        target: '_blank',
                    })}
                    design="primary"
                    endIcon={<PlusIcon width="16px" height="16px" />}
                    onClick={onOpenAttendModal}
                >
                    {t('event_quick_attend_cta')}
                </Button>
            );
    }
};
